import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { BaseResponse } from "../../../shared/types";
import {
  AssignResidentRequest,
  AssignResidentResponse,
  GetResidentsParams,
  GetUsersParams,
  ResidentPeriodsResponse,
  ResidentResponse,
  ResidentsResponse,
  UsersResponse,
} from "./types";
import { ResidentCaliber } from "../../admin-events";

export const adminCommunitiesApi = createApi({
  reducerPath: "adminCommunitiesApi",
  baseQuery: baseApiQuery,
  tagTypes: ["CommunityUsers", "Residents", "ResidentPeriods"],
  endpoints: (builder) => ({
    // Get community users with filtering
    getCommunityUsers: builder.query<UsersResponse, GetUsersParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append("search", params.search);
        if (params.phoneNumber)
          queryParams.append("phoneNumber", params.phoneNumber);
        if (params.role) queryParams.append("role", params.role);
        if (params.blocked !== undefined)
          queryParams.append("blocked", params.blocked.toString());
        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.pageToken) queryParams.append("pageToken", params.pageToken);
        if (params.limit) queryParams.append("limit", params.limit.toString());

        return `/admin/communities/users${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["CommunityUsers"],
    }),

    // Get residents with filtering
    getResidents: builder.query<ResidentsResponse, GetResidentsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.search) queryParams.append("search", params.search);
        if (params.cities && params.cities.length > 0) {
          params.cities.forEach((cityId) => {
            queryParams.append("cities", cityId.toString());
          });
        }
        if (params.calibers && params.calibers.length > 0) {
          params.calibers.forEach((caliber) => {
            queryParams.append("calibers", caliber);
          });
        }
        if (params.blocked !== undefined)
          queryParams.append("blocked", params.blocked.toString());
        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.pageToken) queryParams.append("pageToken", params.pageToken);
        if (params.limit) queryParams.append("limit", params.limit.toString());

        return `/admin/communities/residents${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["Residents"],
    }),

    // Get resident details by ID
    getUserById: builder.query<ResidentResponse, string>({
      query: (id) => `/admin/communities/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Residents", id }],
    }),

    // Assign resident role to user
    assignResident: builder.mutation<
      AssignResidentResponse,
      { id: string; data: AssignResidentRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/communities/users/${id}/assign-resident`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "CommunityUsers",
        "Residents",
        { type: "ResidentPeriods", id },
      ],
    }),

    assignResidentExternalPayment: builder.mutation<
      AssignResidentResponse,
      { id: string; data: AssignResidentRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/communities/users/${id}/assign-resident-external-payment`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "CommunityUsers",
        "Residents",
        { type: "ResidentPeriods", id },
      ],
    }),

    // Get resident periods
    getResidentPeriods: builder.query<ResidentPeriodsResponse, string>({
      query: (id) => `/admin/communities/users/${id}/resident-periods`,
      providesTags: (_result, _error, id) => [{ type: "ResidentPeriods", id }],
    }),

    // Update resident caliber
    updateResidentCaliber: builder.mutation<
      BaseResponse,
      { id: string; caliber: ResidentCaliber }
    >({
      query: ({ id, caliber }) => ({
        url: `/admin/communities/residents/${id}/caliber/${caliber}`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Residents", id },
        "Residents",
      ],
    }),

    // Block user account
    blockUser: builder.mutation<BaseResponse, string>({
      query: (id) => ({
        url: `/admin/communities/users/${id}/block`,
        method: "POST",
      }),
      invalidatesTags: ["CommunityUsers"],
    }),

    // Unblock user account
    unblockUser: builder.mutation<BaseResponse, string>({
      query: (id) => ({
        url: `/admin/communities/users/${id}/unblock`,
        method: "POST",
      }),
      invalidatesTags: ["CommunityUsers"],
    }),

    // Export users to Excel
    exportUsersToXlsx: builder.query<Blob, void>({
      query: () => ({
        url: "/admin/communities/users/export/xlsx",
        method: "GET",
        responseHandler: async (response) => {
          return response.blob();
        },
      }),
    }),
    // Export users to Excel
    exportResidentsToXlsx: builder.query<Blob, void>({
      query: () => ({
        url: "/admin/communities/residents/export/xlsx",
        method: "GET",
        responseHandler: async (response) => {
          return response.blob();
        },
      }),
    }),
  }),
});

export const {
  useGetCommunityUsersQuery,
  useLazyGetCommunityUsersQuery,
  useGetResidentsQuery,
  useLazyGetResidentsQuery,
  useGetUserByIdQuery,
  useAssignResidentMutation,
  useAssignResidentExternalPaymentMutation,
  useGetResidentPeriodsQuery,
  useLazyGetResidentPeriodsQuery,
  useUpdateResidentCaliberMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useExportUsersToXlsxQuery,
  useLazyExportUsersToXlsxQuery,
  useLazyExportResidentsToXlsxQuery,
} = adminCommunitiesApi;
